import { useContext, useEffect } from 'react'

import { Box, CircularProgress, Divider, Typography } from '@material-ui/core'
import { ErrorBoundary } from '@scoir/error-reporter'

import useIsShadow from 'common/use-is-shadow'
import useTitle from 'common/use-title'
// TODO: Update to new UI components - HE-296
import { ComposeContextWrapper, EditContextWrapper } from 'components/ComposeOutreachMessageButton/ContextWrappers'
import { useSupportsLimitedAutomatedOutreach, useSupportsStudentOutreach } from 'store/college/hooks'

import MarketingBanner from '../marketingBanner'
import { OUTREACH_MESSAGE_TYPES } from '../outreach-message-types'
import { ComposeActionTrigger } from './ComposeEditModal/Triggers'
import MessageTableV2 from './MessageTable'
import Filter from './MessageTable/Filter'
import OutreachMessageProvider from './OutreachMessageProvider'
import OutreachMessageContext from './OutreachMessageProvider/Context'
import PriorityReorderModal from './PriorityReorderModal'

const OutreachMessagesWrapper = ({ currentRouteEndpoint }: { currentRouteEndpoint: 'one-time' | 'automated' }) => {
  useTitle('Scoir - Messaging')
  return (
    <OutreachMessageProvider>
      <OutreachMessages route={currentRouteEndpoint} />
    </OutreachMessageProvider>
  )
}

const OutreachMessages = ({ route }: { route: 'one-time' | 'automated' }) => {
  const isUserShadow = useIsShadow()
  const supportsStudentOutreach = useSupportsStudentOutreach()
  const supportsLimitedAutomatedOutreach = useSupportsLimitedAutomatedOutreach()
  const studentOutreachEnabledOrShadow = isUserShadow || supportsStudentOutreach
  const config = OUTREACH_MESSAGE_TYPES[route]
  const { messages, dispatch, filteredMessages, fetchMessageLoading, fetchMessage } = useContext(OutreachMessageContext)
  const routeMessages = filteredMessages
    ? filteredMessages?.[route as keyof typeof messages] ?? []
    : messages?.[route as keyof typeof messages] ?? []
  const numberOfMessages = (messages?.[route as keyof typeof messages] || []).length

  useEffect(() => {
    dispatch({ type: 'update-route', route })
  }, [dispatch, route])

  if (fetchMessageLoading) {
    return (
      <Box
        p={8}
        textAlign="center"
      >
        <CircularProgress
          aria-label="Loading"
          role="status"
          size="50px"
        />
      </Box>
    )
  }

  return (
    <Box>
      <ErrorBoundary>
        {supportsLimitedAutomatedOutreach && (
          <MarketingBanner
            hasMessages={numberOfMessages > 0}
            route={route}
          />
        )}
        <Box
          py={3}
          px={2}
          display="flex"
          justifyContent="space-between"
        >
          <Typography
            variant="h6"
            component="h2"
          >
            {config.header}
          </Typography>
          {studentOutreachEnabledOrShadow && numberOfMessages === 0 && (
            <ComposeContextWrapper onSave={fetchMessage}>
              <ComposeActionTrigger trigger={config.trigger} />
            </ComposeContextWrapper>
          )}
        </Box>
        <Divider light />
        {numberOfMessages > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            py={3}
            px={2}
          >
            <Filter />
            {studentOutreachEnabledOrShadow && (
              <Box
                display="flex"
                gridGap={16}
              >
                {route === 'automated' && <PriorityReorderModal />}
                <ComposeContextWrapper onSave={fetchMessage}>
                  <ComposeActionTrigger trigger={config.trigger} />
                </ComposeContextWrapper>
              </Box>
            )}
          </Box>
        )}
        <Box>
          <EditContextWrapper onSave={fetchMessage}>
            <MessageTableV2
              route={route}
              messages={routeMessages}
              hasMessages={numberOfMessages > 0}
              onChange={fetchMessage}
              studentOutreachEnabledOrShadow={studentOutreachEnabledOrShadow}
              supportsLimitedAutomatedOutreach={supportsLimitedAutomatedOutreach}
            />
          </EditContextWrapper>
        </Box>
      </ErrorBoundary>
    </Box>
  )
}

export default OutreachMessagesWrapper
